import { faVideo, faWifiExclamation } from '@fortawesome/pro-solid-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import { Alert, Icon, InputControl, Radio, RadioGroup, Tag, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state/theme';
import { QualityMode } from '../../video-sdk/core/room/participant/media/types';
import { consolidateQualityMode } from '../../video-sdk/core/utils';

export interface VideoQualitySettingsProps {
  qualityMode: QualityMode;
  onChangeQualityMode?: (mode: QualityMode) => void;
  shouldDisableFields?: boolean;
  shouldShowAutoOptimizationAlert?: boolean;
  storedQualityMode?: QualityMode;
}

export function VideoQualitySettings({
  qualityMode,
  onChangeQualityMode: changeQualityMode,
  shouldDisableFields,
  shouldShowAutoOptimizationAlert,
  storedQualityMode,
}: VideoQualitySettingsProps) {
  const t = useTranslate();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  return (
    <div className='flex flex-col gap-4'>
      <InputControl
        label={t('telehealth-core:settings.quality')}
        className='mb-0'
        labelClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        disabled={shouldDisableFields}
      >
        <RadioGroup
          onChange={(event) => changeQualityMode?.(event.target.value as QualityMode)}
          value={consolidateQualityMode(qualityMode)}
          className='pt-5'
          layout='column'
          name={t('telehealth-core:settings.quality')}
        >
          <Radio
            label={t('telehealth-core:settings.optimizeForQuality')}
            value={QualityMode.High}
            className={cn({ 'rebrand:text-text-inverse rebrand:hover:text-text-inverse': isDarkTheme })}
          />
          <div className='flex flex-wrap items-center gap-1 sm:gap-2'>
            <Radio
              label={t('telehealth-core:settings.optimizeForPerformance')}
              value={QualityMode.Low}
              className={cn({ 'rebrand:text-text-inverse rebrand:hover:text-text-inverse': isDarkTheme })}
            />
            <Tag
              className='inline-flex gap-2 w-content items-center'
              use='neutral'
              aria-label={t('telehealth-core:settings.checkThisBoxIfYouHavePoorConnectivity')}
            >
              <Icon icon={faWifiExclamation} aria-hidden />
              <span>{t('telehealth-core:settings.forLowConnectivity')}</span>
            </Tag>
          </div>
        </RadioGroup>
      </InputControl>
      {shouldShowAutoOptimizationAlert &&
        // Prevents the alert from showing if the user has already set their
        // own quality mode to low I.e. the quality changes that are reflected
        // back by the remote participant won't result in this alert showing
        storedQualityMode !== QualityMode.Low && (
          <Alert
            className='bg-neutral-200'
            heading={t('telehealth-core:settings.aParticipantHasOptimizedTheSessionVideoForPerformance')}
            icon={faVideo}
            use='info'
          >
            {t('telehealth-core:settings.youMayUpdateThisSettingForYourself')}
          </Alert>
        )}
    </div>
  );
}
